// Kokoro voice IDs — pick distinct voices per crew member
const VOICE_MAP: Record<string, string> = {
  // Bridge Crew
  "Vasek":    "am_adam",
  "Okafor":   "bf_emma",
  "Toraq":    "bm_george",
  "Shin":     "af_sky",
  "Reyes":    "af_bella",
  "Calloway": "am_michael",
  // Computer
  "Computer": "af_nicole",
};

const NPC_VOICE_POOL = ["bm_lewis", "bf_isabella", "am_echo", "af_sarah", "bm_daniel", "bf_lily"];

type LoadingState = "idle" | "loading" | "ready" | "error";
type LoadingListener = (state: LoadingState, progress?: number) => void;

interface QueuedAudio {
  speaker: string;
  text: string;
  voice: string;
}

let nextMessageId = 0;

class TTSService {
  private enabled = false;
  private audioQueue: QueuedAudio[] = [];
  private currentAudio: HTMLAudioElement | null = null;
  private isPlaying = false;
  private npcVoiceAssignments = new Map<string, string>();
  private npcPoolIndex = 0;

  private worker: Worker | null = null;
  private loadingState: LoadingState = "idle";
  private loadingListeners = new Set<LoadingListener>();
  private pendingCallbacks = new Map<number, (blob: Blob | null) => void>();

  constructor() {
    const saved = localStorage.getItem("tts_enabled");
    if (saved === "true") {
      this.enabled = true;
    }
  }

  onLoadingChange(listener: LoadingListener): () => void {
    this.loadingListeners.add(listener);
    return () => this.loadingListeners.delete(listener);
  }

  getLoadingState(): LoadingState {
    return this.loadingState;
  }

  private setLoadingState(state: LoadingState, progress?: number) {
    this.loadingState = state;
    for (const listener of this.loadingListeners) {
      listener(state, progress);
    }
  }

  private ensureWorker(): Worker {
    if (!this.worker) {
      this.worker = new Worker(
        new URL("../workers/kokoroWorker.ts", import.meta.url),
        { type: "module" },
      );

      this.worker.onmessage = (e: MessageEvent) => {
        const { type } = e.data;

        if (type === "loading-progress") {
          const pct = e.data.total > 0 ? e.data.loaded / e.data.total : 0;
          this.setLoadingState("loading", pct);
        } else if (type === "ready") {
          this.setLoadingState("ready");
        } else if (type === "error") {
          console.warn("[TTS] Worker error:", e.data.error);
          this.setLoadingState("error");
        } else if (type === "audio") {
          const cb = this.pendingCallbacks.get(e.data.id);
          this.pendingCallbacks.delete(e.data.id);
          cb?.(e.data.blob);
        } else if (type === "audio-error") {
          console.warn("[TTS] Generation error:", e.data.error);
          const cb = this.pendingCallbacks.get(e.data.id);
          this.pendingCallbacks.delete(e.data.id);
          cb?.(null);
        }
      };
    }
    return this.worker;
  }

  loadModel(): void {
    if (this.loadingState === "ready" || this.loadingState === "loading") return;
    this.setLoadingState("loading");
    const worker = this.ensureWorker();
    worker.postMessage({ type: "load" });
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (enabled) {
      this.loadModel();
    } else {
      this.stop();
      this.audioQueue = [];
    }
    localStorage.setItem("tts_enabled", String(enabled));
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  getVoice(speaker: string): string {
    if (VOICE_MAP[speaker]) return VOICE_MAP[speaker];

    if (this.npcVoiceAssignments.has(speaker)) {
      return this.npcVoiceAssignments.get(speaker)!;
    }

    const voice = NPC_VOICE_POOL[this.npcPoolIndex % NPC_VOICE_POOL.length];
    this.npcPoolIndex++;
    this.npcVoiceAssignments.set(speaker, voice);
    return voice;
  }

  speak(speaker: string, text: string): void {
    if (!this.enabled) return;
    if (!text || text.trim().length === 0) return;

    const voice = this.getVoice(speaker);
    this.audioQueue.push({ speaker, text, voice });

    if (!this.isPlaying) {
      this.processQueue();
    }
  }

  private async processQueue(): Promise<void> {
    if (this.audioQueue.length === 0) {
      this.isPlaying = false;
      return;
    }

    this.isPlaying = true;
    const item = this.audioQueue.shift()!;

    try {
      const blob = await this.generateAudio(item.text, item.voice);

      if (!blob) {
        this.processQueue();
        return;
      }

      const audioUrl = URL.createObjectURL(blob);
      this.currentAudio = new Audio(audioUrl);

      this.currentAudio.onended = () => {
        URL.revokeObjectURL(audioUrl);
        this.currentAudio = null;
        this.processQueue();
      };

      this.currentAudio.onerror = () => {
        console.warn("[TTS] Audio playback error");
        URL.revokeObjectURL(audioUrl);
        this.currentAudio = null;
        this.processQueue();
      };

      await this.currentAudio.play();
    } catch (err) {
      console.warn("[TTS] Failed to generate speech:", err);
      this.processQueue();
    }
  }

  private generateAudio(text: string, voice: string): Promise<Blob | null> {
    return new Promise((resolve) => {
      const id = nextMessageId++;
      const worker = this.ensureWorker();
      this.pendingCallbacks.set(id, resolve);
      worker.postMessage({ type: "speak", id, text, voice });
    });
  }

  stop(): void {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio = null;
    }
    this.audioQueue = [];
    this.isPlaying = false;
  }

  skip(): void {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio = null;
    }
    this.processQueue();
  }

  resetNpcVoices(): void {
    this.npcVoiceAssignments.clear();
    this.npcPoolIndex = 0;
  }
}

export const ttsService = new TTSService();
